import React, { useEffect, useState } from "react";
import CoursesListing from "./Components/StudentCoursesListing";
import {
  useGetStudentByEmailIdQuery,
  useGetCampusQuery,
  useGetClustersByIdsAndCampusQuery,
  useGetAllCoursesByClusterQuery,
  useGetAllCoursesByClusterLazyQuery,
  useGetCourseLazyQuery,
} from "../../graphql/generated.tsx";
import { Alert } from "flowbite-react";
import StudentLayout from "../Student/Layout/index.jsx";
import OpeningUI from "../../components/OpeningUI";
import Loader from "../../components/Loader";
import NetworkIndicator from "./Components/NetworkIndicator.jsx";

function Practice() {
  const [allCoursesData, setAllCoursesData] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  // show intro once per logical page (Practice) during this tab session
  const pageKey = "openingUI:practice";
  const [showIntro, setShowIntro] = useState(() => {
    try {
      const map = window.__openingUIShownPages || {};
      return !map[pageKey];
    } catch (e) {
      return true;
    }
  });
  const email = JSON.parse(localStorage.getItem("user"))?.email;

  const {
    data: studentData,
    loading: studentLoading,
    error: studentError,
  } = useGetStudentByEmailIdQuery({ variables: { email }, skip: showIntro || !email });

  useEffect(() => {
    const t = setTimeout(() => {
      setShowIntro(false);
      try {
        window.__openingUIShownPages = window.__openingUIShownPages || {};
        window.__openingUIShownPages[pageKey] = true;
      } catch (e) {}
    }, 7000);
    return () => clearTimeout(t);
  }, []);
 

  const purchasedCourses =
    studentData?.getStudentByEmailId?.purchasedCourses || [];
  const campusId = studentData?.getStudentByEmailId?.campusId;

  const {
    data: campusData,
    loading: campusLoading,
    error: campusError,
  } = useGetCampusQuery({ variables: { id: campusId }, skip: showIntro || !campusId });


  const campusName = campusData?.campus?.name || "";
  const clusterIds = studentData?.getStudentByEmailId?.clusterIds || [];

  const {
    data: clustersData,
    loading: clustersLoading,
    error: clustersError,
  } = useGetClustersByIdsAndCampusQuery({
    variables: { clusterIds, campusName },
    skip: showIntro || !campusName || clusterIds.length === 0,
  });

  const PAID_CLUSTER_ID = "56eed915-cb4f-4bf6-ae18-18a1ad18f281";
  const { data: paidCoursesData } = useGetAllCoursesByClusterQuery({
    variables: {
      clusterId: PAID_CLUSTER_ID,
      campusName: "BT",
    },
    skip: showIntro,
  });

  const purchasedCourseIds = purchasedCourses.map((course) => course.course_id);
  const clusterCourseIds = allCoursesData.flatMap((cluster) =>
    cluster.courses.map((course) => course.id)
  );

  const filteredOnlineCourses =
    paidCoursesData?.getAllCoursesByCluster?.filter(
      (course) =>
        !purchasedCourseIds.includes(course.id) &&
        !clusterCourseIds.includes(course.id)
    ) || [];

  const [getCoursesByCluster] = useGetAllCoursesByClusterLazyQuery();
  const [getCourse] = useGetCourseLazyQuery();
  
  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        if (clustersData?.getClustersByIdsAndCampus?.length > 0) {
          const coursesPromises = clustersData.getClustersByIdsAndCampus.map(
            (cluster) =>
              getCoursesByCluster({
                variables: { clusterId: cluster.id, campusName },
              }).then((response) => ({
                cluster_id: cluster.id,
                clusterName: cluster.name,
                courses: response.data?.getAllCoursesByCluster || [],
              }))
          );

          const allCourses = await Promise.all(coursesPromises);

          const purchasedCoursesPromises = purchasedCourses.map(
            async (purchased) => {
              const response = await getCourse({
                variables: { id: purchased.course_id },
              });

              return {
                id: purchased.course_id,
                name: response.data?.getCourse?.name || "Purchased Course",
                description: response.data?.getCourse?.description || "",
                short_description: response.data?.getCourse?.short_description,
                time_period: response.data?.getCourse?.time_period,
                level: response.data?.getCourse?.level,
                isPurchased: true,
              };
            }
          );

          const purchasedCoursesData = await Promise.all(
            purchasedCoursesPromises
          );

          const purchasedCoursesCluster = {
            cluster_id: PAID_CLUSTER_ID,
            clusterName: "Purchased Courses",
            courses: purchasedCoursesData,
          };

          setAllCoursesData([...allCourses, purchasedCoursesCluster]);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [clustersData, campusName, getCoursesByCluster, getCourse]);

  if (showIntro) {
    return (
      <StudentLayout backgroundClass="bg-white">
        <OpeningUI />
      </StudentLayout>
    );
  }

  const loader = (
    <div className="flex items-center justify-center h-screen bg-white">
    
    </div>
  );

  const _rawErr = studentError || campusError || clustersError;
  const _raw = _rawErr?.graphQLErrors?.[0]?.message || _rawErr?.message || "";
  const _isContextCause = _raw.includes(":") && !_raw.includes(" ");
  const _parsed = _isContextCause
    ? _raw.split(":")[1].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : _raw;
  const _displayMessage =
    _parsed && _parsed.trim().length > 0
      ? _parsed
      : "Error loading data. Please try again later.";

  const errorMessage = (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <Alert color="failure">
        <span>{_displayMessage}</span>
      </Alert>
    </div>
  );

  return (
    <StudentLayout backgroundClass="bg-white">
      <NetworkIndicator />
      <div className="p-5 space-y-8 rounded-lg max-md:m-5 lg:m-10 sm:m-4 max-md:ml-5 bg-white ">
        {/* Enhanced Heading */}
        <div className="p-6 space-y-4 text-center max-md:p-4">
          <h1 className="text-5xl font-extrabold tracking-wide text-primary max-md:text-3xl">
            Explore All Courses 🚀
          </h1>
          <p className="text-lg text-gray-600 max-md:text-base sm:text-sm">
            Browse through a variety of courses tailored for your success in
            your enrolled clusters.
          </p>
        </div>

        {/* Call-to-Action Section */}
        <div className="flex items-center justify-between p-6 bg-blue-100 rounded-lg shadow-lg max-md:flex-col max-md:space-y-4">
          <div className="space-y-2 max-md:text-center">
            <h2 className="text-2xl font-bold text-primary max-md:text-xl">
              Looking for something extra?
            </h2>
            <p className="text-primary max-md:text-sm sm:text-xs">
              Don't miss out on our specially designed online courses to take
              your skills to the next level!
            </p>
          </div>
          <button
            onClick={() =>
              document
                .getElementById("online-courses-section")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="px-6 py-3 font-medium text-white transition rounded-lg shadow bg-primary max-md:w-full sm:text-sm"
          >
            Discover Courses
          </button>
        </div>

        {/* Loader/Error Handling */}
        {studentLoading || campusLoading || clustersLoading || loadingCourses
          ? loader
          : null}
        {studentError || campusError || clustersError ? errorMessage : null}

        {/* All Courses Section */}
        {allCoursesData.map(
          (clusterCourses, index) =>
            clusterCourses.courses.length > 0 && (
              <div key={index} className="space-y-3 mt-14">
                <h2 className="pl-4 text-3xl font-semibold text-primary border-primary max-md:text-xl">
                  {clusterCourses.clusterName}
                </h2>
                <div>
                  <CoursesListing
                    courseData={clusterCourses.courses}
                    clusterId={clusterCourses.cluster_id}
                    student_id={studentData?.getStudentByEmailId?.id}
                  />
                </div>
              </div>
            )
        )}

        {filteredOnlineCourses.length !== 0 && (
          <div className="space-y-3" id="online-courses-section">
            <h2 className="text-3xl font-semibold text-primary border-primary pl-4 max-md:text-xl mt-[54px]">
              Recommended Courses
            </h2>
            <div>
              <CoursesListing
                courseData={filteredOnlineCourses}
                clusterId={PAID_CLUSTER_ID}
                student_id={studentData?.getStudentByEmailId?.id}
                paid
              />
            </div>
          </div>
        )}
      </div>

      {/* Online Courses Section */}
      {/* {filteredOnlineCourses.length !== 0 && (
        <div
          id="online-courses-section"
          className="p-5 space-y-8 rounded-lg max-md:m-5 lg:m-10 sm:m-4 max-md:ml-5 bg-white"
        >
          <div className="p-6 space-y-2 text-center max-md:p-4">
            <h1 className="text-5xl font-extrabold text-primary max-md:text-3xl">
              Discover Your Next Big Opportunity 🌟
            </h1>
            <div className="mb-4 text-center">
              <p className="text-lg font-medium text-gray-700 max-md:text-base sm:text-sm">
                Transform your career with expertly crafted courses designed for
                success.
              </p>
              <p className="mt-1 text-sm text-gray-500 max-md:text-xs">
                Stay ahead of the curve with exclusive content, practical
                insights, and guidance from industry leaders.
              </p>
              <p className="mt-1 text-sm text-gray-500 max-md:text-xs">
                Start your journey today—don’t let the chance to achieve your
                dreams pass you by.
              </p>
            </div>
          </div>
          <CoursesListing
            courseData={filteredOnlineCourses}
            clusterId={PAID_CLUSTER_ID}
            student_id={studentData?.getStudentByEmailId?.id}
            paid
          />
        </div>
      )} */}
    </StudentLayout>
  );
}

export default Practice;
